/**
 * Agent audit-log document schema and paths.
 *
 * Every `processEmail` run appends one append-only log entry recording what the
 * agent decided and whether it acted. This is the audit trail that answers
 * "what did the system do?" — especially important during the dry-run period.
 *
 * Server-written only. Base schema uses {@link FirestoreTimestampSchema} so the
 * admin/ SDK module can extend it with its own timestamp transform.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../../types';
import type {UniqueStringId} from '../../../types/branded';
import {
  AGENT_BUCKET_ID,
  AGENT_LOGS_SUBCOLLECTION,
  getBucketDocPath,
} from '../reportBucket';
import {GateDecisionSchema, ProposedActionSchema} from './agentConfig';

/**
 * Firestore document id of an agent log entry
 * (`reports/_agent/logs/{logId}`).
 */
export type AgentLogId = UniqueStringId<'AgentLogId'>;

/**
 * Base schema for an agent log entry at `reports/_agent/logs/{logId}`.
 *
 * `executed` distinguishes a real auto-action from a dry-run "would have done":
 * when `dryRun` is `true`, `decision` may be `auto` while `executed` is
 * `false`.
 */
export const AgentLogDocumentSchema = z
  .object({
    /** Id of the email this entry is about (`emails/{emailId}`). */
    emailId: z.string(),
    /**
     * Identifier of the model/provider used to classify (e.g.
     * `openai:gpt-4o-mini`). Free-form so the swappable classifier can record
     * whatever it used.
     */
    modelVersion: z.string(),
    /** Classification category assigned, or `null` if classification failed. */
    category: z.string().nullable(),
    /**
     * Classifier confidence that `courseId` was correct (route decision), in
     * `[0, 1]`, or `null` if classification failed.
     */
    courseConfidence: z.number().min(0).max(1).nullable(),
    /**
     * Classifier confidence in the proposed action (gate decision), in
     * `[0, 1]`, or `null` if classification failed.
     */
    actionConfidence: z.number().min(0).max(1).nullable(),
    /**
     * Classifier confidence that `category` is correct, in `[0, 1]`, or `null`
     * if classification failed. Absent on log entries written before the
     * taxonomy work. Independent of `actionConfidence`.
     */
    categoryConfidence: z.number().min(0).max(1).nullable().optional(),
    /** The action the agent proposed, or `null` if none was proposed. */
    proposedAction: ProposedActionSchema.nullable(),
    /** The gate's decision for this email. */
    decision: GateDecisionSchema,
    /** Whether the config was in dry-run mode for this run. */
    dryRun: z.boolean(),
    /**
     * Whether the proposed action was actually executed. Always `false` for
     * escalations and for dry-run auto-decisions.
     */
    executed: z.boolean(),
    /**
     * Id of the report produced by this run, or `null` when it produced none.
     * Reports are bucket-scoped, so this id addresses a document only in
     * combination with this entry's `courseId` — see
     * `getOnPlatformReportDocumentPath` in `documents/report`.
     */
    reportId: z.string().nullable(),
    /**
     * Course bucket the email was routed to (`reports/{courseId}/…`), or `null`
     * when it stayed in the bulk `_uncategorized` bucket.
     */
    courseId: z.string().nullable(),
    /**
     * The raw `courseId` the classifier suggested — recorded even when it was
     * rejected (not an active course) and the email fell back to bulk. `null`
     * when the classifier suggested no course or classification failed.
     */
    suggestedCourseId: z.string().nullable(),
    /**
     * Outcome of the auto-reply gate/executor for this run:
     * - `sent` — a canned acknowledgement was written to `mail/`;
     * - `dry_run` — the gate said send, but the config was in dry-run so nothing
     *   was sent (log-only);
     * - `skipped` — the auto-reply gate declined (see `decideAutoReply`);
     * - `failed` — the gate said send but the `mail/` write threw.
     * `null`/absent on entries written before auto-reply existed and on error
     * runs where classification never completed.
     */
    autoReplyStatus: z
      .enum(['sent', 'dry_run', 'skipped', 'failed'])
      .nullable()
      .optional(),
    /**
     * Id of the `mail/{mailDocId}` document created by an auto-reply
     * (`autoReplyStatus === 'sent'`), or `null`/absent otherwise. Links to the
     * extension-managed delivery record.
     */
    autoReplyMailDocId: z.string().nullable().optional(),
    /** Error message when the run failed, otherwise `null`. */
    error: z.string().nullable(),
    /** Server time the entry was written. */
    timestamp: FirestoreTimestampSchema,
  })
  .loose();

/** Inferred type for {@link AgentLogDocumentSchema}. */
export type AgentLogDocument = z.infer<typeof AgentLogDocumentSchema>;

/**
 * Collection path for agent audit logs, nested under the reserved `_agent`
 * bucket.
 *
 * @returns The Firestore collection path `reports/_agent/logs`.
 * @throws Never — pure string construction.
 */
export function getAgentLogsCollectionPath(): string {
  return `${getBucketDocPath({bucketId: AGENT_BUCKET_ID})}/${AGENT_LOGS_SUBCOLLECTION}`;
}

/**
 * Single agent-log document path.
 *
 * @param args.logId - The log entry's Firestore id.
 * @returns Firestore doc path `reports/_agent/logs/{logId}`.
 * @throws Never — pure string construction.
 */
export function getAgentLogDocumentPath({logId}: {logId: string}): string {
  return `${getAgentLogsCollectionPath()}/${logId}`;
}
